/**
 * Bell-icon backing hook (#571 PR 3, ADR 0012).
 *
 * Two concerns, kept in one hook because they share polling cadence
 * and cache lifecycle:
 *   - The unread COUNT — refreshed every 30s while the page is visible
 *     so the badge stays current without WebSockets. Pauses when the
 *     tab is hidden (visibilitychange) to avoid waking sleeping tabs.
 *   - The recent FEED — fetched on dropdown open, refetched on
 *     mutations (markRead / markAllRead) so the UI reflects the new
 *     state without waiting for the next poll.
 *
 * Polling cadence chosen at 30s as a balance: too short and the
 * server takes a constant trickle of read-side load from every open
 * tab; too long and the badge feels stale during a flurry of run
 * failures. 30s matches the typical "did my CI just go red" feedback
 * loop. If realtime ever matters more than this, swap to a server-
 * sent stream — the hook's surface is stable.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { notificationsAPI, type NotificationItem } from '@/lib/api'

const POLL_INTERVAL_MS = 30_000
const FEED_LIMIT = 20

export interface UseNotificationsReturn {
  /** Current unread count for the bell badge. Starts at 0; updates on
   * the polling tick and on every mutation. */
  unreadCount: number
  /** Most-recent feed entries. Empty until `refreshFeed()` is called
   * (typically on dropdown open) — the hook does NOT auto-fetch the
   * feed on mount to avoid a wasted round-trip for users who never
   * open the dropdown. */
  items: NotificationItem[]
  loadingFeed: boolean
  /** Manual feed refetch — call on dropdown open and after each
   * mutation. */
  refreshFeed: () => Promise<void>
  /** Mark one entry read. Optimistic update on the local state +
   * count; reconciles on the next refresh tick. */
  markRead: (id: string) => Promise<void>
  /** Bulk mark-all-read. Optimistic. */
  markAllRead: () => Promise<void>
  /** Dismiss (hard-delete) one entry. Optimistically drops it from
   * the feed and decrements the count if it was unread; reconciles
   * on failure. */
  dismiss: (id: string) => Promise<void>
  /** Clear (hard-delete) every entry. Optimistically empties the feed
   * and zeroes the count; reconciles on failure. */
  clearAll: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const [unreadCount, setUnreadCount] = useState(0)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loadingFeed, setLoadingFeed] = useState(false)

  // Use a ref for the polling guard so the interval handler isn't
  // capturing stale state. The handler runs every 30s; setState
  // inside it is fine, but we don't want to re-create the interval
  // on every count change.
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const fetchCount = useCallback(async () => {
    try {
      const c = await notificationsAPI.unreadCount()
      if (mountedRef.current) setUnreadCount(c)
    } catch {
      // Swallow — the badge becoming briefly stale is preferable to
      // a thrown error from a background poll surfacing in console.
      // The next tick will recover.
    }
  }, [])

  const refreshFeed = useCallback(async () => {
    // Guard the leading state write too. After a notification click,
    // the component may unmount mid-flight (navigation runs first,
    // then markRead's rollback path can call refreshFeed) — without
    // this guard the setLoadingFeed(true) would warn about updates
    // on an unmounted component. PR #627 Copilot review.
    if (!mountedRef.current) return
    setLoadingFeed(true)
    try {
      const fetched = await notificationsAPI.list({ limit: FEED_LIMIT })
      if (mountedRef.current) setItems(fetched)
    } finally {
      if (mountedRef.current) setLoadingFeed(false)
    }
  }, [])

  // Initial count fetch + polling interval. Pauses while the tab is
  // hidden — restored on visibilitychange.
  useEffect(() => {
    let intervalHandle: ReturnType<typeof setInterval> | null = null

    function start() {
      void fetchCount()
      intervalHandle = setInterval(() => { void fetchCount() }, POLL_INTERVAL_MS)
    }
    function stop() {
      if (intervalHandle) {
        clearInterval(intervalHandle)
        intervalHandle = null
      }
    }
    function onVisibility() {
      if (document.hidden) stop()
      else if (!intervalHandle) start()
    }

    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fetchCount])

  const markRead = useCallback(async (id: string) => {
    // Optimistic — flip locally first so the UI doesn't lag on a
    // slow round-trip; the server is the source of truth on the
    // next tick.
    setItems(prev => prev.map(item =>
      item._id === id && !item.read ? { ...item, read: true, readAt: new Date().toISOString() } : item,
    ))
    setUnreadCount(prev => Math.max(0, prev - 1))
    try {
      await notificationsAPI.markRead(id)
    } catch {
      // Roll back by re-fetching authoritative state.
      await fetchCount()
      await refreshFeed()
    }
  }, [fetchCount, refreshFeed])

  const markAllRead = useCallback(async () => {
    setItems(prev => prev.map(item => item.read ? item : { ...item, read: true, readAt: new Date().toISOString() }))
    setUnreadCount(0)
    try {
      await notificationsAPI.markAllRead()
    } catch {
      await fetchCount()
      await refreshFeed()
    }
  }, [fetchCount, refreshFeed])

  const dismiss = useCallback(async (id: string) => {
    // Optimistic — drop the row locally first. Whether it was unread
    // is computed HERE, before the updates, so the badge decrement is
    // its own pure `setState` call — no side effect inside the
    // `setItems` updater (which can re-run under StrictMode/concurrent
    // rendering). Mirrors `markRead`'s two-separate-updates shape.
    const wasUnread = items.some(item => item._id === id && !item.read)
    setItems(prev => prev.filter(item => item._id !== id))
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1))
    try {
      await notificationsAPI.dismissNotification(id)
    } catch {
      await fetchCount()
      await refreshFeed()
    }
  }, [items, fetchCount, refreshFeed])

  const clearAll = useCallback(async () => {
    setItems([])
    setUnreadCount(0)
    try {
      await notificationsAPI.clearAllNotifications()
    } catch {
      await fetchCount()
      await refreshFeed()
    }
  }, [fetchCount, refreshFeed])

  return { unreadCount, items, loadingFeed, refreshFeed, markRead, markAllRead, dismiss, clearAll }
}
